import { Component, EventEmitter, Input, OnDestroy, OnInit, Output, ViewChild } from '@angular/core';
import { AbstractControl, FormBuilder, FormGroup, UntypedFormArray, Validators } from '@angular/forms';
import { ActivatedRoute } from '@angular/router';
import { smoothOpenClose } from 'app/animations/smooth.animations';
import {
	CountryISOCode,
	GroupedUltimateBeneficialOwnerDto,
	KycHttpService,
	PepType,
	SetKycDataRequest,
	TrapetsHttpService,
} from 'app/api';
import { OrganizationService } from 'app/services';
import { WhiteLabelClientSettingsService } from 'app/services/white-label-client-settings.service';
import { atLeastOnePEPValidator, atLeastOneUBOValidator, pepTypeValidator } from 'app/shared/validators';
import { BehaviorSubject, combineLatest, Observable, Subject } from 'rxjs';
import { map, take, takeUntil, tap } from 'rxjs/operators';
import { CountryNameService } from '../../../services/country-name.service';
import { CountrySelectorComponent } from '../country-selector/country-selector.component';

@Component({
	selector: 'app-kyc',
	templateUrl: './kyc.component.html',
	styleUrls: ['./kyc.component.scss'],
	animations: [smoothOpenClose],
	standalone: false,
})
export class KycComponent implements OnInit, OnDestroy {
	@Input() kyc: SetKycDataRequest = null;
	@Input() shouldRedirectToNewLoanStep = false;
	@Input() showSaveButton = true;
	@Output() saveKyc: EventEmitter<{ organizationId: string; data: SetKycDataRequest }> = new EventEmitter<any>();
	ubosDataSource$ = new BehaviorSubject<GroupedUltimateBeneficialOwnerDto[]>([]);
	kycForm: FormGroup;
	addUBOModalVisible = false;
	protected readonly destroy$ = new Subject();
	ubosLoading = false;
	loading = false;
	countryCode: string;
	organizationId: string;
	isSoleTrader$: Observable<boolean>;

	@Input() PEPTypeOptions: { value: PepType; text: string }[] = [
		{ value: PepType.Pep, text: 'kyc.pep-type.pep' },
		{ value: PepType.PepRelative, text: 'kyc.pep-type.pep-relative' },
		{ value: PepType.PepCloseAssociate, text: 'kyc.pep-type.pep-close-associate' },
	];

	@ViewChild(CountrySelectorComponent)
	private countrySelectorComponent: CountrySelectorComponent;

	constructor(
		protected formBuilder: FormBuilder,
		protected route: ActivatedRoute,
		protected trapetsService: TrapetsHttpService,
		protected kycHttpService: KycHttpService,
		protected organizationService: OrganizationService,
		protected countryNameService: CountryNameService,
		protected clientSettingsService: WhiteLabelClientSettingsService
	) {}

	ngOnInit(): void {
		this.organizationId = this.organizationService.getOrganizationId();
		this.isSoleTrader$ = this.organizationService
			.getOrganization()
			.pipe(map(organization => organization.is_sole_trader ?? false));
		this.countryCode = this.clientSettingsService.getSettings()?.countryCode ?? CountryISOCode.SE;
		this.setUboDataSource();
		this.setFormValues();
	}

	setFormValues() {
		this.initializeForm();

		this.ubosDataSource$.pipe(takeUntil(this.destroy$)).subscribe(ubos => {
			this.setFormUBOs(ubos);
			this.watchIsAnyOwnerPEPValueChanges();
		});
	}

	initializeForm(): void {
		const ubos = this.kyc ? this.kyc.groupedUltimateBeneficialOwners : [];
		this.kycForm = this.formBuilder.group({
			ubos: [ubos],
			isAnyOwnerPEP: [this.kyc && ubos?.some(o => o.is_pep), { validators: [Validators.required] }],
			isPEP: [true, { validators: [Validators.required] }],
		});
	}

	setUboDataSource() {
		if (!this.kyc?.groupedUltimateBeneficialOwners) {
			this.setUboDataSourceFromTrapets();
		} else {
			this.setUboDataSourceFromKyc();
		}
	}

	setUboDataSourceFromTrapets() {
		this.ubosLoading = true;
		this.kycHttpService.getUltimateBeneficialOwners(this.organizationId).subscribe(data => {
			this.ubosLoading = false;
			//only set data from trapets if there is no data from kyc
			if (this.ubosDataSource$.value.length === 0) {
				this.ubosDataSource$.next(data || []);
			}
		});
	}

	setUboDataSourceFromKyc() {
		this.ubosLoading = true;
		this.ubosDataSource$.next(this.kyc?.groupedUltimateBeneficialOwners || []);
		this.ubosLoading = false;
	}

	watchIsAnyOwnerPEPValueChanges() {
		combineLatest([this.isAnyOwnerPEP.valueChanges, this.isSoleTrader$])
			.pipe(takeUntil(this.destroy$))
			.subscribe(([value, isSoleTrader]) => {
				if (!value) {
					this.ubos.controls.forEach(ubo => {
						ubo.patchValue({ is_pep: false });
					});
				}
				if (value && isSoleTrader) {
					this.ubos.controls.forEach(ubo => {
						ubo.patchValue({ is_pep: true });
					});
				}
				this.ubos.updateValueAndValidity();
			});
	}

	setFormUBOs(ubos: GroupedUltimateBeneficialOwnerDto[]) {
		this.kycForm.setControl(
			'ubos',
			this.formBuilder.array(
				ubos?.map(ubo =>
					this.formBuilder.group(
						{
							first_name: [ubo.first_name ? ubo.first_name : ''],
							last_name: [ubo.last_name ? ubo.last_name : ''],
							ssn: [ubo.ssn],
							is_pep: [!!ubo.is_pep],
							hashed_ssn: ubo.hashed_ssn,
							taxpayer_identification_number: [ubo.taxpayer_identification_number],
							hashed_taxpayer_identification_number: ubo.hashed_taxpayer_identification_number,
							pep_type: [this.PEPTypeOptions.find(pepOption => pepOption?.value == ubo.pep_type) || ''],
						},
						{ validators: [pepTypeValidator()] }
					)
				) || [],
				{ validators: [atLeastOneUBOValidator(), atLeastOnePEPValidator(() => this.isAnyOwnerPEP.value)] }
			)
		);
	}

	deleteUBO(i) {
		this.ubosDataSource$
			.pipe(
				take(1),
				map(ubos => {
					ubos.splice(i, 1);
					return ubos;
				})
			)
			.subscribe(ubos => this.ubosDataSource$.next(ubos));
	}

	addUBO(ubo: GroupedUltimateBeneficialOwnerDto) {
		this.ubosDataSource$
			.pipe(
				take(1),
				map(ubos => {
					ubos.push(ubo);
					return ubos;
				}),
				tap(_ => (this.addUBOModalVisible = false))
			)
			.subscribe(ubos => this.ubosDataSource$.next(ubos));
	}

	get isAnyOwnerPEP(): AbstractControl {
		return this.kycForm.get('isAnyOwnerPEP');
	}

	get ubos(): UntypedFormArray {
		return this.kycForm.get('ubos') as UntypedFormArray;
	}

	save() {
		this.loading = true;
		this.kycForm.markAllAsTouched();
		if (this.kycForm.valid && this.countrySelectorComponent.validateForm()) {
			const data = this.mapFormToKycDataRequest();
			this.saveKyc.emit({
				organizationId: this.organizationId,
				data: data,
			});
			this.loading = false;
		} else {
			this.loading = false;
		}
	}

	mapFormToKycDataRequest(): SetKycDataRequest {
		return {
			associatedCountries: this.countrySelectorComponent.returnData().associatedCountries,
			doesOperateOutsideOwnCountry: this.countrySelectorComponent.returnData().doesOperateOutsideOwnCountry,
			groupedUltimateBeneficialOwners: this.ubos?.value?.map(u => {
				return {
					first_name: u.first_name,
					last_name: u.last_name,
					ssn: u.ssn,
					taxpayer_identification_number: u.taxpayer_identification_number,
					hashed_ssn: u.hashed_ssn,
					hashed_taxpayer_identification_number: u.hashed_taxpayer_identification_number,
					is_pep: u.is_pep,
					pep_type: u.pep_type ? u.pep_type.value : PepType.NotPep,
					country: this.countryCode,
				};
			}),
		};
	}

	isFormValid(): boolean {
		this.kycForm.markAllAsTouched();
		return this.kycForm.valid;
	}

	ngOnDestroy(): void {
		this.destroy$.next(undefined);
		this.destroy$.complete();
	}
}
